import { useCallback, useEffect, useMemo } from 'react';
import { captureException } from '@sentry/nextjs';
import { MY_SCHEDULE_PAGE } from 'constants/pages';
import {
  LessonParticipants,
  useGetActiveOwnerUpcomingLessonsByIdLazyQuery,
} from 'types/generated/client';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import { useViewer } from 'hooks/useViewer';
import CouchLamp from 'svg/CouchLamp';
import Location from 'svg/Location';
import { AvatarProps } from 'screens/CoachLoggedInScreen/props';
import { ButtonLink } from 'components/Button';
import {
  formatTime,
  getCapacityDisplay,
  getLocationDisplay,
  getRowClassName,
  getTimeUntilLesson,
} from '../../utils';
import { TodayLessonsSkeleton } from '../Skeletons';

const Avatar = ({ src, alt, className = '', style }: AvatarProps) => (
  <div
    className={`rounded-full bg-cover bg-center bg-no-repeat ${className}`}
    style={{ backgroundImage: `url('${src}')`, ...style }}
  />
);

const TodayLessons = () => {
  const viewer = useViewer();

  const [
    getActiveOwnerUpcomingLessonsById,
    { loading: upcomingLessonsLoading, data: upcomingLessonsData },
  ] = useGetActiveOwnerUpcomingLessonsByIdLazyQuery();

  const fetchTodayLessons = useCallback(async () => {
    try {
      await getActiveOwnerUpcomingLessonsById({
        variables: {
          ownerUserId: viewer.userId,
          limit: 10, // Get more lessons to filter for today
        },
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
      });
    } catch (err) {
      captureException(err);
    }
  }, [viewer.userId, getActiveOwnerUpcomingLessonsById]);

  useEffect(() => {
    if (viewer.userId) {
      fetchTodayLessons();
    }
  }, [viewer.userId, fetchTodayLessons]);

  const todayLessons = useMemo(() => {
    if (!upcomingLessonsData?.lessons) return [];

    const today = new Date();
    const startOfToday = new Date(today.setHours(0, 0, 0, 0));
    const endOfToday = new Date(today.setHours(23, 59, 59, 999));

    return upcomingLessonsData.lessons.filter((lesson) => {
      const lessonDate = new Date(lesson.startDateTime);
      return lessonDate >= startOfToday && lessonDate <= endOfToday;
    });
  }, [upcomingLessonsData]);

  if (upcomingLessonsLoading) {
    return <TodayLessonsSkeleton />;
  }

  const renderMobileParticipants = (participants: LessonParticipants[]) => {
    const hasMultipleParticipants = participants.length > 1;

    if (hasMultipleParticipants) {
      return (
        <div className="flex items-center justify-end -space-x-2">
          {participants.slice(0, 3).map((participant, index) => (
            <Avatar
              key={participant.id}
              src={getProfileImageUrlOrPlaceholder({
                path: participant.userProfile?.profileImagePath,
              })}
              alt={participant.userProfile?.preferredName || 'Unknown'}
              className="relative h-7 w-7 border-2 border-white"
              style={{ zIndex: 10 + index }}
            />
          ))}
          {participants.length > 3 && (
            <div
              className="max-sm:typography-product-chips-filters relative flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-brand-gray-200"
              style={{ zIndex: 6 }}
            >
              +{participants.length - 3}
            </div>
          )}
        </div>
      );
    }

    if (participants.length > 0) {
      return (
        <div className="flex items-center justify-end gap-2">
          <Avatar
            src={getProfileImageUrlOrPlaceholder({
              path: participants[0].userProfile?.profileImagePath,
            })}
            alt={participants[0].userProfile?.preferredName || 'Unknown'}
            className="h-7 w-7"
          />
          <span className="typography-product-element-label max-sm:typography-product-chips-filters max-w-20 truncate">
            {participants[0].userProfile?.preferredName || 'Unknown'}
          </span>
        </div>
      );
    }

    return <span className="typography-product-element-label text-right">No participants</span>;
  };

  const renderDesktopParticipants = (participants: LessonParticipants[]) => {
    const hasMultipleParticipants = participants.length > 1;

    if (hasMultipleParticipants) {
      return (
        <div className="flex items-center -space-x-1">
          {participants.slice(0, 4).map((participant, index) => (
            <Avatar
              key={participant.id}
              src={getProfileImageUrlOrPlaceholder({
                path: participant.userProfile?.profileImagePath,
              })}
              alt={participant.userProfile?.preferredName || 'Unknown'}
              className="relative h-8 w-8 xl:h-9 xl:w-9"
              style={{ zIndex: 10 + index }}
            />
          ))}
          {participants.length > 4 && (
            <div
              className="typography-product-element-label relative flex h-8 w-8 items-center justify-center rounded-full bg-brand-gray-200 xl:h-9 xl:w-9"
              style={{ zIndex: 6 }}
            >
              +{participants.length - 4}
            </div>
          )}
        </div>
      );
    }

    if (participants.length > 0) {
      return (
        <div className="flex items-center gap-3">
          <Avatar
            src={getProfileImageUrlOrPlaceholder({
              path: participants[0].userProfile?.profileImagePath,
            })}
            alt={participants[0].userProfile?.preferredName || 'Unknown'}
            className="h-8 w-8 xl:h-9 xl:w-9"
          />
          <span className="typography-product-element-label truncate">
            {participants[0].userProfile?.preferredName || 'Unknown'}
          </span>
        </div>
      );
    }

    return <span className="typography-product-element-label">No participants</span>;
  };

  return (
    <div className="container mx-auto mt-6 max-sm:mt-5 max-sm:px-4">
      <h5 className="typography-product-heading-compact-desktop max-sm:typography-product-subheading mb-ds-xl max-sm:mb-ds-md">
        Today's Lessons
      </h5>

      {todayLessons.length > 0 ? (
        <div className="space-y-3">
          {todayLessons.map((lesson) => {
            const participants = (lesson.participants || []) as LessonParticipants[];

            return (
              <div key={lesson.id} className={`rounded-xl border ${getRowClassName(lesson.type)}`}>
                {/* Mobile Layout (< sm) - 3 columns: Time | Capacity | Participants */}
                <div className="grid min-h-12 grid-cols-[90px_50px_1fr] items-center gap-3 px-4  sm:hidden">
                  {/* Time */}
                  <div className="typography-product-card-title-desktop max-sm:typography-product-chips-filters">
                    {formatTime(lesson.startDateTime)}
                  </div>

                  {/* Capacity */}
                  <div className="typography-product-chips-filters text-center  text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary ">
                    {getCapacityDisplay(lesson)}
                  </div>

                  {/* Participants */}
                  <div className="max-sm:typography-product-tabbar-mobile min-w-0">
                    {renderMobileParticipants(participants)}
                  </div>
                </div>

                {/* Tablet Layout (sm - lg) - 5 columns */}
                <div className="hidden min-h-16 grid-cols-[100px_80px_1fr_140px_120px] items-center gap-4 px-4 py-3 sm:grid lg:hidden">
                  {/* Time */}
                  <div className="typography-product-card-title-desktop">
                    {formatTime(lesson.startDateTime)}
                  </div>

                  {/* Duration */}
                  <div className="typography-product-chips-filters text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    {getTimeUntilLesson(lesson.startDateTime)}
                  </div>

                  {/* Title */}
                  <div className="typography-product-card-title-desktop truncate">
                    {lesson.title}
                  </div>

                  {/* Location */}
                  <div className="flex min-w-0 items-center gap-1">
                    <Location className="h-4 w-4 shrink-0 text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary" />
                    <span className="typography-product-chips-filters truncate text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                      {getLocationDisplay(lesson)}
                    </span>
                  </div>

                  {/* Participants */}
                  <div className="min-w-0">{renderDesktopParticipants(participants)}</div>
                </div>

                {/* Desktop Layout (lg+) - 6 columns */}
                <div className="hidden min-h-16 grid-cols-[120px_100px_240px_160px_90px_1fr] items-center gap-4 px-4 py-3 lg:grid xl:grid-cols-[130px_110px_280px_180px_300px_1fr]">
                  {/* Time */}
                  <div className="typography-product-card-title-desktop">
                    {formatTime(lesson.startDateTime)}
                  </div>

                  {/* Duration */}
                  <div className="typography-product-chips-filters text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    {getTimeUntilLesson(lesson.startDateTime)}
                  </div>

                  {/* Title */}
                  <div className="typography-product-card-title-desktop truncate">
                    {lesson.title}
                  </div>

                  {/* Location */}
                  <div className="flex min-w-0 items-center gap-1">
                    <Location className="h-4 w-4 shrink-0 text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary" />
                    <span className="typography-product-chips-filters truncate text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                      {getLocationDisplay(lesson)}
                    </span>
                  </div>

                  {/* Capacity */}
                  <div className="typography-product-chips-filters text-center text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    {getCapacityDisplay(lesson)}
                  </div>

                  {/* Participants */}
                  <div className="min-w-0">{renderDesktopParticipants(participants)}</div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 py-8">
          <CouchLamp className="h-10 w-10" />
          <span className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            No lessons today. There's nothing wrong with a little break.
          </span>
        </div>
      )}

      <div className="mt-6 flex justify-end max-sm:mt-4">
        <ButtonLink
          variant="secondary"
          sizeDesktop="md"
          sizeMobile="sm"
          className="w-fit"
          href={MY_SCHEDULE_PAGE}
        >
          View calendar
        </ButtonLink>
      </div>
    </div>
  );
};

export default TodayLessons;
